import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, ArrowLeft, BookOpen, LayoutList, Link2, List } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyLedgerState } from '../../components/ledger/EmptyLedgerState';
import { ResizablePanel } from '../../components/ui/ResizablePanel';
import { AccountList } from '../../components/accounting/AccountList';
import { LedgerView } from '../../components/accounting/LedgerView';
import { BalanceView } from '../../components/ledger/BalanceView';
import { ReconciliationPanel } from '../../components/reconciliation/ReconciliationPanel';
import { AccountEditModal } from '../../components/ledger/AccountEditModal';
import { PlanComptableModal } from '../../components/ledger/PlanComptableModal';
import { LedgerFilters, type LedgerFilters as Filters } from '../../components/accounting/LedgerFilters';
import { useLedger } from '../../hooks/useLedger';
import { useAccounts } from '../../hooks/useAccounts';
import { useAccountAccess } from '../../hooks/useAccountAccess';
import { useAccountingStore } from '../../stores/accountingStore';
import { useNavigationWithReturn } from '../../hooks/useNavigationWithReturn';
import type { Account } from '../../types/accounting';
import type { TrialBalanceEntry } from '../../types/filters';

/**
 * Helper pour obtenir le solde brut d'une entrée de balance
 * Compatible avec l'ancien et le nouveau format
 */
const getBalanceRaw = (entry: TrialBalanceEntry): number => {
  if (typeof entry.balance === 'number') {
    return entry.balance;
  }
  return entry.balance?.raw ?? (entry.debit - entry.credit);
};

export function LedgerPage() {
  const [searchParams] = useSearchParams();
  const { goBack, hasReturnPath, getNavigationState } = useNavigationWithReturn();
  
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showBalance, setShowBalance] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [showPlanComptable, setShowPlanComptable] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    accountType: 'all',
    startDate: '',
    endDate: '',
    balanceType: 'all'
  });


  const { 
    trialBalance,
    trialBalanceResponse,
    accountMovements,
    loading,
    loadAccountMovements
  } = useLedger();

  // Protection contre les données undefined
  const safeTrialBalance = Array.isArray(trialBalance) ? trialBalance : [];

  const { 
    addAccount,
    updateAccount,
    deleteAccount,
    accounts: rawAccounts,
    loading: accountsLoading,
    pagination: accountsPagination,
    loadAccounts
  } = useAccounts();

  const { canEditAccounts, canViewBalance } = useAccountAccess();
  const { mode } = useAccountingStore();
  
  // État de navigation (pour le bouton retour)
  const navigationState = getNavigationState();
  const showBackButton = hasReturnPath();

  // Callback pour changement de page - stable grâce à useRef
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  
  const paginationRef = useRef(accountsPagination);
  paginationRef.current = accountsPagination;

  const handlePageChange = useCallback((page: number) => {
    loadAccounts({ 
      page, 
      pageSize: paginationRef.current.pageSize,
      search: filtersRef.current.search || undefined,
      type: filtersRef.current.accountType !== 'all' ? filtersRef.current.accountType : undefined
    });
  }, [loadAccounts]);

  // Callback pour changement de filtres - recharger depuis le backend
  const handleFiltersChange = useCallback((newFilters: Filters) => {
    setFilters(newFilters);
    // Recharger les comptes avec les nouveaux filtres
    loadAccounts({
      page: 1, // Retour à la première page lors d'un changement de filtre
      pageSize: paginationRef.current.pageSize,
      search: newFilters.search || undefined,
      type: newFilters.accountType !== 'all' ? newFilters.accountType : undefined
    });
  }, [loadAccounts]);

  // Callback pour recherche de compte (depuis AccountList)
  const handleAccountSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search }));
    loadAccounts({
      page: 1,
      pageSize: paginationRef.current.pageSize,
      search: search || undefined,
      type: filtersRef.current.accountType !== 'all' ? filtersRef.current.accountType : undefined
    });
  }, [loadAccounts]);

  // Ref pour éviter de re-sélectionner le compte à chaque render
  const accountSelectedRef = useRef<string | null>(null);

  // Charger le compte depuis l'URL si spécifié (navigation depuis widget)
  useEffect(() => {
    const accountCode = searchParams.get('account');
    const accountId = searchParams.get('accountId');
    
    if (!accountCode && !accountId) return;
    if (rawAccounts.length === 0) return;
    
    // Éviter de re-sélectionner si déjà traité pour ces params
    const paramsKey = `${accountCode || ''}_${accountId || ''}`;
    if (accountSelectedRef.current === paramsKey) return;
    
    // Pré-remplir le filtre de recherche avec le code de compte
    if (accountCode) {
      setFilters(prev => ({ ...prev, search: accountCode }));
    }
    
    // Trouver le compte par ID ou par code (fallback)
    let account = null;
    
    if (accountId) {
      account = rawAccounts.find(a => a.id === accountId);
    }
    
    // Fallback: chercher par code exact puis par préfixe
    if (!account && accountCode) {
      account = rawAccounts.find(a => a.code === accountCode) ||
                rawAccounts.find(a => a.code?.startsWith(accountCode));
    }
    
    if (account) {
      accountSelectedRef.current = paramsKey;
      handleAccountSelect(account);
    }
  }, [searchParams, rawAccounts]);

  // Hook pour le dialog de confirmation
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const handleAccountSelect = async (account: Account) => {
    setSelectedAccount(account);
    // Réinitialiser le mode lettrage si le nouveau compte n'est pas lettrable
    if (!account.allowReconciliation) setShowReconciliation(false);
    await loadAccountMovements(account.id);
  };

  const handleAddAccount = async (data: Partial<Account>) => {
    try {
      await addAccount({ ...data, standard: mode });
      setShowAccountForm(false);
      setSelectedAccount(null);
      // Recharger la liste des comptes après création
      await loadAccountMovements('');
    } catch (error) {
      console.error('Failed to add account:', error);
      // L'erreur sera gérée et affichée dans le modal
      throw error;
    }
  };

  const handleUpdateAccount = async (data: Partial<Account>) => {
    if (!selectedAccount) return;
    try {
      await updateAccount(selectedAccount.id, data);
      setShowAccountForm(false);
      setSelectedAccount(null);
    } catch (error) {
      console.error('Failed to update account:', error);
      throw error;
    }
  };

  const handleDeleteAccount = async (account: Account) => {
    const confirmed = await confirm({
      title: 'Supprimer le compte',
      message: (
        <div>
          <p>Êtes-vous sûr de vouloir supprimer ce compte ?</p>
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="font-mono text-sm font-semibold text-primary">{account.code}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">{account.name}</p>
          </div>
          <p className="mt-3 text-amber-600 dark:text-amber-400 text-xs">
            ⚠️ Cette action est irréversible
          </p>
        </div>
      ),
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await deleteAccount(account.id);
        toast.success(`Compte ${account.code} supprimé avec succès`);
        if (selectedAccount?.id === account.id) {
          setSelectedAccount(null);
        }
      } catch (error) {
        toast.error('Erreur lors de la suppression du compte');
      }
    }
  };

  // Protection contre les données undefined pour les comptes bruts
  const safeAccounts = Array.isArray(rawAccounts) ? rawAccounts : [];

  // Les comptes sont déjà filtrés par le backend via loadAccounts
  // On applique uniquement les filtres de balance localement (car ce sont des calculs côté client)
  const filteredAccounts = safeAccounts
    .filter(account => {
      if (!account || !account.code || !account.name) return false;
      
      // Filtre par type de solde (appliqué localement car basé sur les données de balance)
      if (filters.balanceType !== 'all' && safeTrialBalance.length > 0) {
        const balance = safeTrialBalance.find(item => item.account.id === account.id);
        if (!balance) return false;

        const netBalance = getBalanceRaw(balance);
        if (filters.balanceType === 'debit' && netBalance <= 0) return false;
        if (filters.balanceType === 'credit' && netBalance >= 0) return false;
      }

      return true;
    });

  // Ajuster la pagination quand le filtre balanceType est actif côté client
  // Le serveur ne connaît pas ce filtre, donc les totaux doivent être ajustés
  const effectivePagination = useMemo(() => {
    const isBalanceFilterActive = filters.balanceType !== 'all' && safeTrialBalance.length > 0;
    if (!isBalanceFilterActive) {
      return accountsPagination;
    }
    // Quand un filtre client-side réduit les résultats, on ne peut pas savoir
    // le vrai total côté serveur. On ajuste pour montrer le compte filtré.
    return {
      ...accountsPagination,
      total: filteredAccounts.length,
      totalPages: 1,
      page: 1
    };
  }, [accountsPagination, filters.balanceType, safeTrialBalance.length, filteredAccounts.length]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {/* Bouton retour si navigation depuis un widget */}
          {showBackButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={goBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">
                {navigationState?.fromLabel || 'Retour'}
              </span>
            </Button>
          )}
          <h1 className="text-2xl font-bold text-foreground">Grand Livre</h1>
          {/* Bouton Plan Comptable — accès rapide */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPlanComptable(true)}
            className="flex items-center gap-1.5 text-xs"
            title="Voir le plan comptable"
          >
            <List className="h-3.5 w-3.5" />
            <span className="hidden md:inline">Plan Comptable</span>
          </Button>
          {/* Indication du compte recherché si présent */}
          {navigationState?.accountCode && (
            <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
              Compte: {navigationState.accountCode}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {canViewBalance && (
            <Button
              variant={showBalance ? 'primary' : 'outline'}
              icon={showBalance ? LayoutList : BookOpen}
              onClick={() => setShowBalance(!showBalance)}
              className="transition-all"
            >
              <span className="hidden sm:inline">
                {showBalance ? 'Voir le grand livre' : 'Voir la balance'}
              </span>
              <span className="sm:hidden">
                {showBalance ? 'Grand livre' : 'Balance'}
              </span>
            </Button>
          )}
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => {
              if (!canEditAccounts) {
                toast.error('Permissions insuffisantes pour créer un compte');
                return;
              }
              setSelectedAccount(null);
              setShowAccountForm(true);
            }}
            disabled={!canEditAccounts}
            title={!canEditAccounts ? 'Permissions insuffisantes' : 'Créer un nouveau compte'}
          >
            <span className="hidden sm:inline">Nouveau Compte</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
        </div>
      </div>

      <LedgerFilters onFilterChange={handleFiltersChange} />

      {showBalance ? (
        <Card title="Balance">
          <BalanceView accounts={safeTrialBalance} loading={loading} />
        </Card>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6" style={{ minHeight: 'calc(100vh - 300px)' }}>
          {/* Panneau de liste des comptes - redimensionnable sur desktop */}
          <ResizablePanel
            minWidth={280}
            maxWidth={550}
            defaultWidth={380}
            storageKey="ledger-accounts-panel"
            className="w-full lg:w-auto"
          >
            <Card title="Liste des comptes" className="h-full flex flex-col">
              <AccountList
                accounts={filteredAccounts}
                onEdit={(account) => {
                  if (canEditAccounts) {
                    setSelectedAccount(account);
                    setShowAccountForm(true);
                  }
                }}
                onDelete={canEditAccounts ? handleDeleteAccount : undefined}
                onSelect={handleAccountSelect}
                selectedAccount={selectedAccount}
                loading={loading || accountsLoading}
                pagination={effectivePagination}
                onPageChange={handlePageChange}
                onSearch={handleAccountSearch}
              />
            </Card>
          </ResizablePanel>

          {/* Panneau des mouvements / lettrage - prend l'espace restant */}
          <div className="flex-1 min-w-0">
            {selectedAccount ? (
              <div className="space-y-3">
                {/* Bascule Lettrage — visible pour les comptes tiers (4xx) ou explicitement activé */}
                {selectedAccount.code && /^4[0-2]/.test(selectedAccount.code) && (
                  <div className="flex justify-end">
                    <Button
                      variant={showReconciliation ? 'primary' : 'outline'}
                      size="sm"
                      icon={Link2}
                      onClick={() => {
                        if (!selectedAccount.allowReconciliation) {
                          toast.error(
                            `Le lettrage n'est pas activé sur ce compte. ` +
                            `Éditez le compte et cochez « Autoriser le lettrage ».`
                          );
                          return;
                        }
                        setShowReconciliation(!showReconciliation);
                      }}
                    >
                      {showReconciliation ? 'Mouvements' : 'Lettrage'}
                    </Button>
                  </div>
                )}
                {showReconciliation && selectedAccount.allowReconciliation ? (
                  <ReconciliationPanel
                    accountId={selectedAccount.id}
                    accountCode={selectedAccount.code}
                    accountName={selectedAccount.name}
                  />
                ) : (
                  <LedgerView
                    accountId={selectedAccount.id}
                    accountCode={selectedAccount.code}
                    accountName={selectedAccount.name}
                    entries={accountMovements[selectedAccount.id] || []}
                    loading={loading}
                  />
                )}
              </div>
            ) : (
              <Card className="h-full min-h-[400px] flex items-center justify-center">
                <EmptyLedgerState type="no-selection" />
              </Card>
            )}
          </div>
        </div>
      )}

      <AccountEditModal
        isOpen={showAccountForm}
        onClose={() => {
          setShowAccountForm(false);
          setSelectedAccount(null);
        }}
        account={selectedAccount}
        onSave={selectedAccount ? handleUpdateAccount : handleAddAccount}
      />

      {/* Modal Plan Comptable */}
      <PlanComptableModal
        isOpen={showPlanComptable}
        onClose={() => setShowPlanComptable(false)}
        onSelectAccount={(account) => {
          setShowPlanComptable(false);
          handleAccountSelect(account);
        }}
      />

      {/* Dialog de confirmation */}
      {ConfirmDialogComponent}
    </div>
  );
}